'use client'
// =============================================================================
// HudFrame — cadre HUD réutilisable de toutes les cartes du tableau de bord.
// Le SVG dessine ce que le CSS ne sait pas faire (diagonales des coins coupés, équerres lumineuses, segments décoratifs) ;
// le CSS (hud-shell.css, .hud-frame) gère fond, bordure droite, double filet, espacements, adaptation à la largeur et lueur.
// Les coins sont des SVG de taille fixe : le cadre s'adapte à n'importe quelle dimension sans déformer le dessin.
// =============================================================================
import type { CSSProperties, ReactNode } from 'react'
import { HudIcon, type HudIconName } from './HudIcon'

const CUT = 14   // doit rester égal à --hud-cut dans hud-shell.css

function Corners() {
  return (
    <>
      {/* coins coupés (haut-gauche, bas-droite) : la diagonale que le clip-path ôte à la bordure */}
      <svg className="hud-c hud-c-tl" width={CUT} height={CUT} viewBox={`0 0 ${CUT} ${CUT}`} aria-hidden>
        <line x1="0" y1={CUT} x2={CUT} y2="0" /><circle cx="2" cy={CUT - 2} r="1.2" className="dot" />
      </svg>
      <svg className="hud-c hud-c-br" width={CUT} height={CUT} viewBox={`0 0 ${CUT} ${CUT}`} aria-hidden>
        <line x1="0" y1={CUT} x2={CUT} y2="0" /><circle cx={CUT - 2} cy="2" r="1.2" className="dot" />
      </svg>
      {/* équerres lumineuses (haut-droite, bas-gauche) */}
      <svg className="hud-c hud-c-tr" width="20" height="20" viewBox="0 0 20 20" aria-hidden><path d="M0 1.5H18.5V20" className="bracket" /><path d="M6 4.5H15.5V14" className="inner" /></svg>
      <svg className="hud-c hud-c-bl" width="20" height="20" viewBox="0 0 20 20" aria-hidden><path d="M1.5 0V18.5H20" className="bracket" /><path d="M4.5 6V15.5H14" className="inner" /></svg>
      {/* petits segments décoratifs sur l'arête haute */}
      <svg className="hud-seg" width="46" height="4" viewBox="0 0 46 4" aria-hidden><rect x="0" y="1" width="18" height="2" /><rect x="22" y="1" width="8" height="2" /><rect x="34" y="1" width="3" height="2" /><rect x="41" y="1" width="3" height="2" /></svg>
    </>
  )
}

export interface HudFrameProps {
  title?: ReactNode
  icon?: HudIconName
  /** Lien ou bouton d'action, aligné à droite de l'en-tête. */
  action?: ReactNode
  /** Teinte du cadre : cyan par défaut, ou une couleur CSS (ex. 'var(--gold)'). */
  accent?: string
  className?: string
  style?: CSSProperties
  id?: string
  children: ReactNode
}

export function HudFrame({ title, icon, action, accent, className, style, id, children }: HudFrameProps) {
  return (
    <section id={id} className={`hud-frame${className ? ` ${className}` : ''}`} style={{ ...(accent ? { ['--hud-accent' as string]: accent } : {}), ...style }}>
      <Corners />
      {(title || action) && (
        <header className="hud-frame-head">
          <h3>{icon && <HudIcon name={icon} size={20} />}<span>{title}</span></h3>
          {action && <div className="hud-frame-action">{action}</div>}
        </header>
      )}
      <div className="hud-frame-body">{children}</div>
    </section>
  )
}
